import { useCallback, useEffect, useMemo, useState } from "react";
import { isAuthenticated } from "../apicalls/auth";
import {
  downloadMarketingSegmentCsv,
  getMarketingSegmentRows,
  getMarketingSegments,
} from "../apicalls/marketingSegments";
import "./style/AdminDemos.css";
import "./style/MarketingSegments.css";

const DEFAULT_SEGMENTS = [
  {
    key: "group_level_prospects",
    label: "Unrated Group Level Prospects",
    description: "Players without a declared rating who joined levelled group classes.",
  },
  {
    key: "frequent_group_players",
    label: "Frequent Group Players",
    description: "Players with several group class bookings.",
  },
  {
    key: "lapsed_group_players",
    label: "Lapsed Group Players",
    description: "Players whose last group class booking is older than the selected window.",
  },
  {
    key: "demo_racket_followups",
    label: "Demo Racket Follow-ups",
    description: "Demo racket customers who can be followed up for equipment or classes.",
  },
];

const LEVEL_FILTERS = [
  { value: "", label: "All levels" },
  { value: "2.5", label: "2.5 Beginner" },
  { value: "3.0", label: "3.0 Advanced Beginner" },
  { value: "3.5", label: "3.5 Intermediate" },
  { value: "4.0", label: "4.0 Advanced" },
  { value: "4.5", label: "4.5 Advanced Plus" },
];

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString();
}

function joinValues(values) {
  if (!Array.isArray(values) || values.length === 0) return "-";
  return values.filter(Boolean).join(", ") || "-";
}

function ratingSummary(row) {
  const ratings = [
    row.current_rating ? `Current ${row.current_rating}` : null,
    row.starting_rating ? `Start ${row.starting_rating}` : null,
    row.self_rated_seed ? `Self ${row.self_rated_seed}` : null,
  ].filter(Boolean);
  return ratings.length ? ratings.join(" / ") : "Not declared";
}

function saveBlobFile(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export default function MarketingSegments() {
  const token = isAuthenticated()?.access_token;
  const [segments, setSegments] = useState(DEFAULT_SEGMENTS);
  const [activeSegment, setActiveSegment] = useState(DEFAULT_SEGMENTS[0].key);
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({
    search: "",
    level: "",
    minClasses: 3,
    lapsedDays: 90,
    perPage: 100,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const selectedSegment = useMemo(
    () => segments.find((segment) => segment.key === activeSegment) || segments[0],
    [activeSegment, segments],
  );

  const activeFilters = useMemo(() => {
    const next = {
      search: filters.search,
      level: filters.level,
      perPage: filters.perPage,
    };
    if (activeSegment === "frequent_group_players") {
      next.minClasses = filters.minClasses;
    }
    if (activeSegment === "lapsed_group_players") {
      next.lapsedDays = filters.lapsedDays;
    }
    return next;
  }, [activeSegment, filters]);

  useEffect(() => {
    if (!token) return;
    getMarketingSegments(token).then((data) => {
      if (data && Array.isArray(data.segments) && data.segments.length) {
        setSegments(data.segments);
        if (!data.segments.some((segment) => segment.key === activeSegment)) {
          setActiveSegment(data.segments[0].key);
        }
      }
    });
  }, [activeSegment, token]);

  const fetchRows = useCallback(async () => {
    if (!token || !activeSegment) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getMarketingSegmentRows(activeSegment, token, activeFilters);
      if (data && Array.isArray(data.rows)) {
        setRows(data.rows);
      } else {
        setRows([]);
        setError("Failed to load marketing segment.");
      }
    } catch (err) {
      setRows([]);
      setError(err.message || "Failed to load marketing segment.");
    }
    setLoading(false);
  }, [activeFilters, activeSegment, token]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleDownload = async () => {
    if (!token || !activeSegment) return;
    setDownloading(true);
    setError(null);
    try {
      const response = await downloadMarketingSegmentCsv(activeSegment, token, activeFilters);
      if (!response.ok) {
        throw new Error("Failed to download CSV.");
      }
      const blob = await response.blob();
      saveBlobFile(blob, `${activeSegment}.csv`);
    } catch (err) {
      setError(err.message || "Failed to download CSV.");
    }
    setDownloading(false);
  };

  if (!token) return null;

  return (
    <main>
      <div className="marketing-segments">
        <div className="marketing-segments-header">
          <div>
            <h1>Marketing Segments</h1>
            <p>{selectedSegment?.description}</p>
          </div>
          <button className="btn-primary" onClick={handleDownload} disabled={downloading}>
            {downloading ? "Downloading..." : "Download CSV"}
          </button>
        </div>

        <div className="segment-tabs" aria-label="Marketing segments">
          {segments.map((segment) => (
            <button
              key={segment.key}
              className={`filter-btn ${activeSegment === segment.key ? "active" : ""}`}
              onClick={() => setActiveSegment(segment.key)}
              type="button"
            >
              {segment.label}
            </button>
          ))}
        </div>

        <div className="admin-demos-filters">
          <div className="filter-group">
            <input
              type="text"
              placeholder="Search name, email, phone..."
              value={filters.search}
              onChange={(event) => updateFilter("search", event.target.value)}
            />
          </div>
          <div className="filter-group">
            <select
              className="marketing-level-select"
              aria-label="Class level"
              value={filters.level}
              onChange={(event) => updateFilter("level", event.target.value)}
            >
              {LEVEL_FILTERS.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          {activeSegment === "frequent_group_players" && (
            <div className="filter-group compact-input">
              <span>Min classes:</span>
              <input
                type="number"
                min="1"
                max="100"
                value={filters.minClasses}
                onChange={(event) => updateFilter("minClasses", event.target.value)}
              />
            </div>
          )}
          {activeSegment === "lapsed_group_players" && (
            <div className="filter-group compact-input">
              <span>Lapsed days:</span>
              <input
                type="number"
                min="7"
                max="1000"
                value={filters.lapsedDays}
                onChange={(event) => updateFilter("lapsedDays", event.target.value)}
              />
            </div>
          )}
        </div>

        {loading && <div className="spinner" />}
        {error && <div className="error-message">{error}</div>}

        {!loading && !error && (
          <table className="admin-demos-table marketing-segments-table">
            <thead>
              <tr>
                <th>Player</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Levels / Statuses</th>
                <th>Bookings</th>
                <th>Latest Activity</th>
                <th>Rating</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan="7">No players found</td>
                </tr>
              )}
              {rows.map((row, index) => (
                <tr key={`${row.user_id || row.email || row.phone || "row"}-${index}`}>
                  <td>{row.full_name || "-"}</td>
                  <td>{row.email || "-"}</td>
                  <td>{row.phone || "-"}</td>
                  <td>{joinValues(row.inferred_levels || row.demo_statuses)}</td>
                  <td>{row.class_count || row.demo_count || 0}</td>
                  <td>
                    <div>{formatDate(row.latest_lesson_at || row.latest_demo_at)}</div>
                    <small>
                      {row.latest_lesson_title || row.latest_vendor_name || row.latest_location || ""}
                    </small>
                  </td>
                  <td>{ratingSummary(row)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </main>
  );
}
